import React, { useMemo, useState, useCallback } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  getYear,
  isSameDay,
  startOfDay,
  isBefore,
} from 'date-fns';
import { Button } from '@/components/ui/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Lesson } from '@court-sync/shared';
import type { Coach } from '@court-sync/shared';
import type { LessonSession } from '@court-sync/shared';
import { LessonOccurrenceDayPopover } from './LessonOccurrenceDayPopover';
import {
  LessonCalendarDayPopover,
  lessonCalendarDayPopoverAriaLabel,
} from './LessonCalendarDayPopover';
import type { LessonAvailabilityWarningDays } from '@/utils/coachAvailabilityWarnings';
import {
  lessonCalendarOccurrenceToClickPayload,
  type LessonOccurrenceClickPayload,
  type MakeupSessionRef,
  type ReportedAbsenceSessionRef,
  type ScheduleEnrollmentBookingRef,
  type SchedulePendingBookingRef,
  type RemovedFromSessionRef,
  buildAcademyOccurrencesFromLessonSessions,
  isStandalonePrivateLesson,
  isLessonOccurrenceCancelledOnDate,
  type ScheduleActiveStatusFilter,
  type ScheduleOccurrence,
  type ScheduleOccurrenceSourceType,
} from '@/utils/scheduleOccurrences';
import {
  lessonOccurrenceChipTooltip,
  type LessonCalendarTooltipContext,
} from '@/utils/lessonCalendarTooltip';
import {
  getLessonCalendarChipStyleProps,
  type AgeGroupCalendarColorLookup,
} from '@/utils/lessonSlotColors';
import { getScheduleOccurrenceChipProps } from '@/utils/scheduleOccurrenceChipProps';
import { CalendarChipSportGlyph } from '@/components/sport/CalendarChipSportGlyph';
import type { AcademySport } from '@court-sync/shared';
import { formatTimeHHMM } from '@/utils/timeFormat';

function normalizeSessionTimeForCalendar(t: string | undefined | null): string | undefined {
  if (!t || t === 'undefined') return undefined;
  const s = String(t).trim();
  if (s.length >= 5) return s.slice(0, 5);
  return s || undefined;
}

/** Dark blue accent for “today” + schedule tooltips (stable in light/dark dashboard) */
const CALENDAR_DARK_BLUE = {
  tooltip:
    'z-50 max-w-[min(300px,92vw)] border-0 bg-primary-800 px-3 py-1.5 text-xs font-medium leading-snug text-white shadow-md',
  todayCell:
    'border-primary-800 bg-primary-800/5 ring-1 ring-primary-800/30 dark:bg-primary-800/15 sm:ring-2',
  todayLabel: 'text-primary-800 dark:text-primary-300',
  todayBadge:
    'inline-flex h-5 min-w-[1.25rem] cursor-default items-center justify-center rounded-full bg-primary-800 px-0.5 text-[10px] font-semibold text-white tabular-nums sm:h-6 sm:min-w-[1.5rem] sm:px-1 sm:text-[11px]',
  focusRing: 'focus-visible:ring-primary-800',
  chipRing: 'hover:ring-primary-800 focus:ring-primary-800',
} as const;

function CalendarScheduleHint({
  label,
  children,
}: {
  label: string;
  children: React.ReactElement<{ title?: string }>;
}) {
  return React.cloneElement(children, {
    title: label,
  });
}

function dayCellTooltipLabel(
  day: Date,
  dayOccurrences: LessonOccurrence[],
  isToday: boolean,
  dayPopoverEnabled: boolean,
  tooltipContext?: LessonCalendarTooltipContext
): string {
  const parts = [format(day, 'EEEE, MMMM d, yyyy')];
  if (isToday) parts.push('Today');
  if (dayOccurrences.length > 0) {
    parts.push(
      `${dayOccurrences.length} lesson${dayOccurrences.length === 1 ? '' : 's'} scheduled`
    );
    for (const occ of dayOccurrences.slice(0, 4)) {
      parts.push(`• ${lessonOccurrenceChipTooltip(occ, tooltipContext)}`);
    }
    if (dayOccurrences.length > 4) {
      parts.push(`• +${dayOccurrences.length - 4} more`);
    }
  }
  if (dayPopoverEnabled) parts.push('Click to view lessons on this day');
  return parts.join('\n');
}

function toLessonCalendarDate(
  value: Date | string | { toDate: () => Date } | undefined
): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') return new Date(value);
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate: () => Date }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  try {
    return new Date(value as unknown as string);
  } catch {
    return null;
  }
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export type LessonOccurrence = ScheduleOccurrence & {
  dayOfWeek: number;
  override?: { newDate?: Date | string; [key: string]: unknown };
  isRescheduledReplacement?: boolean;
};

export interface LessonCalendarViewProps {
  lessons: Lesson[];
  terms: any[];
  overrides?: any[];
  /** Make-up credit usage per session (for counts + monthly indicators) */
  makeupSessions?: MakeupSessionRef[];
  pendingScheduleBookings?: SchedulePendingBookingRef[];
  scheduleEnrollmentBookings?: ScheduleEnrollmentBookingRef[];
  removedFromSessionEnrollments?: RemovedFromSessionRef[];
  reportedAbsenceSessions?: ReportedAbsenceSessionRef[];
  coaches?: Coach[];
  /** When set, month cells open session details instead of only the day popover */
  onLessonOccurrenceClick?: (payload: LessonOccurrenceClickPayload) => void;
  /** When set, court hire / closure / play event chips open schedule detail instead of popover */
  onScheduleOccurrenceClick?: (occurrence: LessonOccurrence) => void;
  coachAvailabilityWarnings?: LessonAvailabilityWarningDays;
  /** Called when the user navigates to a new month */
  onMonthChange?: (year: number, month: number) => void;
  /** Materialized sessions (adds replacement dates not on the lesson weekday pattern, e.g. after postpone). */
  lessonSessions?: LessonSession[];
  courtNamesById?: Record<string, string>;
  /** When set (coach My Schedule), filters session chips to roster that includes this coach. */
  scheduleForCoachId?: string;
  /** @deprecated Day cells open the day lessons popover; use onCreateLesson from the header instead */
  onCalendarDayClick?: (date: Date) => void;
  /** When true, day cells do not open the day lessons popover */
  calendarDayClickDisabled?: boolean;
  /** Optional lookups for age group / program labels in chip tooltips */
  tooltipContext?: LessonCalendarTooltipContext;
  /** Age groups for month chip colours (same resolution as weekly slots) */
  ageGroupById?: Record<string, AgeGroupCalendarColorLookup>;
  scheduleActiveStatusFilter?: ScheduleActiveStatusFilter;
  /** Controlled month; when omitted, month state is managed internally */
  currentMonth?: Date;
  /** Called when the displayed month changes (controlled or uncontrolled) */
  onCurrentMonthChange?: (date: Date) => void;
  /** Hide month navigation toolbar (e.g. when rendered in the page header) */
  hideMonthNav?: boolean;
  /** @deprecated Use header toolbar instead */
  toolbarStart?: React.ReactNode;
  showSportGlyphsOnChips?: boolean;
  academySports?: readonly AcademySport[];
  /** Court hire / closure occurrences shown alongside lesson chips. */
  extraOccurrences?: ScheduleOccurrence[];
  sourceTypeFilters?: ScheduleOccurrenceSourceType[];
}

export function LessonCalendarView({
  lessons,
  terms,
  overrides = [],
  makeupSessions = [],
  pendingScheduleBookings,
  scheduleEnrollmentBookings,
  removedFromSessionEnrollments,
  reportedAbsenceSessions = [],
  coaches = [],
  onLessonOccurrenceClick,
  onScheduleOccurrenceClick,
  coachAvailabilityWarnings = new Map(),
  onMonthChange,
  lessonSessions,
  courtNamesById,
  scheduleForCoachId,
  calendarDayClickDisabled = false,
  tooltipContext,
  ageGroupById,
  scheduleActiveStatusFilter = 'active',
  currentMonth: controlledCurrentMonth,
  onCurrentMonthChange,
  hideMonthNav = false,
  toolbarStart,
  showSportGlyphsOnChips = false,
  academySports = [],
  extraOccurrences = [],
  sourceTypeFilters,
}: LessonCalendarViewProps) {
  const isMobile = useIsMobile();
  const maxVisibleOccurrences = isMobile ? 3 : 6;
  const now = new Date();
  const [internalCurrentMonth, setInternalCurrentMonth] = useState(now);
  const currentMonth = controlledCurrentMonth ?? internalCurrentMonth;
  const setCurrentMonth = useCallback(
    (date: Date) => {
      if (controlledCurrentMonth === undefined) {
        setInternalCurrentMonth(date);
      }
      onCurrentMonthChange?.(date);
    },
    [controlledCurrentMonth, onCurrentMonthChange]
  );
  const displayYear = getYear(currentMonth);

  // Monthly grid: one chip per materialized lesson_session (same source as weekly academy/coach views).
  const allOccurrences = useMemo(() => {
    const schedule = buildAcademyOccurrencesFromLessonSessions(
      lessons,
      terms,
      lessonSessions,
      makeupSessions,
      pendingScheduleBookings,
      reportedAbsenceSessions,
      courtNamesById,
      scheduleForCoachId,
      scheduleActiveStatusFilter,
      scheduleEnrollmentBookings,
      removedFromSessionEnrollments
    );

    return schedule.map((occ) => {
      const ymd = format(occ.date, 'yyyy-MM-dd');
      const sess = lessonSessions?.find(
        (s) =>
          s.lessonId === occ.lesson.id &&
          s.sessionDate.replace(/T.*/, '').slice(0, 10) === ymd
      );
      const originalOfPostponed =
        sess && sess.status === 'scheduled'
          ? lessonSessions?.find((s) => s.newSessionId === sess.id && s.status === 'postponed')
          : undefined;

      const override = overrides.find((o) => {
        const orig = toLessonCalendarDate(o.originalDate as Date | string);
        return orig && format(orig, 'yyyy-MM-dd') === ymd && o.lessonId === occ.lesson.id;
      });

      const startTime =
        occ.startTime ??
        (sess ? normalizeSessionTimeForCalendar(sess.startTime) : undefined) ??
        occ.lesson.startTime ??
        '09:00';
      const endTime =
        occ.endTime ??
        (sess ? normalizeSessionTimeForCalendar(sess.endTime) : undefined) ??
        occ.lesson.endTime ??
        '10:00';

      return {
        ...occ,
        dayOfWeek: occ.date.getDay(),
        startTime,
        endTime,
        override,
        isRescheduledReplacement: !!originalOfPostponed,
        isStandalonePrivate:
          occ.isStandalonePrivate ?? isStandalonePrivateLesson(occ.lesson),
      };
    });
  }, [
    lessons,
    terms,
    lessonSessions,
    makeupSessions,
    pendingScheduleBookings,
    scheduleEnrollmentBookings,
    removedFromSessionEnrollments,
    reportedAbsenceSessions,
    courtNamesById,
    overrides,
    scheduleForCoachId,
    scheduleActiveStatusFilter,
  ]);

  const mergedOccurrences = useMemo(() => {
    const facilityExtras = extraOccurrences.map((occ) => ({
      ...occ,
      dayOfWeek: occ.date.getDay(),
      isStandalonePrivate: false,
      isRescheduledReplacement: false,
    })) as LessonOccurrence[];
    const merged = [...allOccurrences, ...facilityExtras];
    if (!sourceTypeFilters?.length) return merged;
    const allowed = new Set(sourceTypeFilters);
    return merged.filter((occ) => allowed.has(occ.sourceType));
  }, [allOccurrences, extraOccurrences, sourceTypeFilters]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const occurrencesByDate = useMemo(() => {
    const map = new Map<string, LessonOccurrence[]>();
    for (const occ of mergedOccurrences) {
      const key = format(occ.date, 'yyyy-MM-dd');
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(occ);
    }
    return map;
  }, [mergedOccurrences]);

  const calendarDays = useMemo(() => {
    const days: Date[] = [];
    let day = calendarStart;
    while (day <= calendarEnd) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [calendarStart, calendarEnd]);

  const isCampOccurrence = (occ: LessonOccurrence) => occ.lesson.lessonType === 'camp';

  const handleOccurrenceChipClick = (
    occ: LessonOccurrence,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    if (occ.sourceType === 'lesson' && onLessonOccurrenceClick) {
      onLessonOccurrenceClick(lessonCalendarOccurrenceToClickPayload(occ));
      return;
    }
    if (occ.sourceType !== 'lesson' && onScheduleOccurrenceClick) {
      onScheduleOccurrenceClick(occ);
    }
  };

  const occurrenceChipOpensDetail = (occ: LessonOccurrence) =>
    (occ.sourceType === 'lesson' && !!onLessonOccurrenceClick) ||
    (occ.sourceType !== 'lesson' && !!onScheduleOccurrenceClick);

  const isOccurrencePast = (occ: LessonOccurrence) =>
    isBefore(startOfDay(occ.date), startOfDay(now));

  const getDotChipProps = (occ: LessonOccurrence) => {
    if (occ.sourceType === 'court_hire' || occ.sourceType === 'closure' || occ.sourceType === 'play_event') {
      const chipProps = getScheduleOccurrenceChipProps(occ, { ageGroupById });
      return { className: chipProps.className, style: chipProps.style };
    }

    const cancelled = isLessonOccurrenceCancelledOnDate(occ.lesson, occ.date, occ.status);
    return getLessonCalendarChipStyleProps(occ.lesson, {
      ageGroupById,
      isPast: isOccurrencePast(occ),
      overrideType: cancelled ? 'cancelled' : occ.status === 'postponed' ? 'postponed' : null,
      isRescheduledReplacement: !!occ.isRescheduledReplacement,
      isStandalonePrivate: !!occ.isStandalonePrivate,
      isCamp: isCampOccurrence(occ),
    });
  };

  const hasCoach = (lesson: Lesson) => {
    if (!lesson.coachIds || lesson.coachIds.length === 0) {
      return false;
    }
    const activeCoaches = lesson.coachIds.some((coachId) => {
      const coach = coaches.find((c) => c.id === coachId);
      return coach?.isActive !== false;
    });
    return activeCoaches;
  };

  const years = useMemo(() => {
    const y = [];
    const current = getYear(now);
    for (let i = current - 2; i <= current + 5; i++) {
      y.push(i);
    }
    return y;
  }, []);

  const canPrev = monthStart > startOfMonth(new Date(displayYear, 0, 1));
  const canNext = monthEnd < endOfMonth(new Date(displayYear, 11, 31));
  const dayPopoverEnabled = !calendarDayClickDisabled;

  const showInlineToolbar = !hideMonthNav || toolbarStart;

  return (
    <div className="w-full space-y-3 sm:space-y-4">
      {showInlineToolbar ? (
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          {!hideMonthNav ? (
            <div
              className="inline-flex shrink-0 items-center overflow-hidden rounded-lg border border-border bg-background shadow-sm"
              role="group"
              aria-label="Calendar month navigation"
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 rounded-none"
                onClick={() => {
                  const newDate = subMonths(currentMonth, 1);
                  setCurrentMonth(newDate);
                  onMonthChange?.(newDate.getFullYear(), newDate.getMonth());
                }}
                disabled={!canPrev}
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex h-8 shrink-0 items-center gap-1.5 border-x border-border px-2 sm:gap-2 sm:px-2.5">
                <span
                  className="inline-flex w-[5.5rem] shrink-0 items-center justify-center text-sm font-semibold sm:w-[6.5rem] sm:text-base"
                  aria-live="polite"
                >
                  {format(currentMonth, 'MMMM')}
                </span>
                <select
                  aria-label="Calendar year"
                  value={displayYear.toString()}
                  onChange={(e) => {
                    const year = parseInt(e.target.value, 10);
                    const newDate = new Date(year, currentMonth.getMonth(), 1);
                    setCurrentMonth(newDate);
                    onMonthChange?.(newDate.getFullYear(), newDate.getMonth());
                  }}
                  className="h-7 w-[5.75rem] shrink-0 border-0 bg-transparent py-0 pl-0 pr-8 text-xs tabular-nums shadow-none focus-visible:ring-0 sm:text-sm [background-position:right_0.5rem_center] [background-size:1rem_1rem]"
                >
                  {years.map((year) => (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 rounded-none"
                onClick={() => {
                  const newDate = addMonths(currentMonth, 1);
                  setCurrentMonth(newDate);
                  onMonthChange?.(newDate.getFullYear(), newDate.getMonth());
                }}
                disabled={!canNext}
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          ) : null}
          {toolbarStart ? (
            <div className="flex w-full min-w-0 items-center gap-2 sm:ml-auto sm:w-auto sm:justify-end sm:gap-3">
              {toolbarStart}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-2 sm:space-y-4">
          <div className="grid grid-cols-7 gap-0.5 text-center text-[9px] font-medium text-gray-600 dark:text-gray-400 sm:gap-1.5 sm:text-xs">
            {DAYS.map((d, i) => (
              <div key={d} className="py-0.5">
                <span className="sm:hidden" aria-hidden>
                  {DAY_LETTERS[i]}
                </span>
                <span className="hidden sm:inline">{d}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5 sm:gap-1.5">
            {calendarDays.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const dayOccurrences = occurrencesByDate.get(key) ?? [];
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isToday = isSameDay(day, new Date());

              const dayCellClassName = `min-h-[3.5rem] rounded-sm border p-0.5 sm:min-h-40 sm:rounded-md sm:p-1.5 md:min-h-48 ${
                isToday
                  ? CALENDAR_DARK_BLUE.todayCell
                  : `${isCurrentMonth ? 'bg-background' : 'bg-muted/30'} border-border`
              } ${dayPopoverEnabled ? `cursor-pointer hover:bg-muted/50 dark:hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 ${CALENDAR_DARK_BLUE.focusRing}` : ''}`;

              const dayCellInner = (
                <>
                  <div
                    className={`mb-0.5 text-[10px] font-medium sm:mb-1 sm:text-xs ${
                      isToday ? CALENDAR_DARK_BLUE.todayLabel : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    <CalendarScheduleHint
                      label={dayCellTooltipLabel(
                        day,
                        dayOccurrences,
                        isToday,
                        dayPopoverEnabled,
                        tooltipContext
                      )}
                    >
                      <span
                        className={
                          isToday
                            ? CALENDAR_DARK_BLUE.todayBadge
                            : 'inline-flex cursor-default tabular-nums leading-none'
                        }
                      >
                        {format(day, 'd')}
                      </span>
                    </CalendarScheduleHint>
                  </div>
                  <div className="flex flex-col gap-px sm:gap-0.5">
                    {dayOccurrences.slice(0, maxVisibleOccurrences).map((occ, idx) => {
                      const chipProps = getDotChipProps(occ);
                      const dotBtnClass = `w-full text-left rounded px-0.5 py-px text-[8px] leading-tight sm:px-1 sm:py-0.5 sm:text-[10px] ${chipProps.className} truncate hover:ring-2 hover:ring-offset-1 ${CALENDAR_DARK_BLUE.chipRing} focus:outline-none focus:ring-2 flex items-center gap-0.5`;
                      const label = `${occ.lessonName || occ.lesson.name} on ${format(occ.date, 'MMM d')}`;
                      const tooltip =
                        occ.sourceType === 'lesson'
                          ? lessonOccurrenceChipTooltip(occ, tooltipContext)
                          : `${occ.lessonName} · ${formatTimeHHMM(occ.startTime)} – ${formatTimeHHMM(occ.endTime)}`;
                      const chipButton = (
                        <button
                          type="button"
                          className={dotBtnClass}
                          style={chipProps.style}
                          aria-label={label}
                          onClick={
                            occurrenceChipOpensDetail(occ)
                              ? (e) => handleOccurrenceChipClick(occ, e)
                              : (e) => e.stopPropagation()
                          }
                        >
                          {(coachAvailabilityWarnings.get(occ.lesson.id)?.has(occ.date.getDay()) ??
                            false) && (
                            <AlertTriangle
                              className="w-2.5 h-2.5 flex-shrink-0 text-amber-200 drop-shadow-sm"
                              aria-hidden
                            />
                          )}
                          {occ.makeupCount && occ.makeupCount > 0 && (
                            <span
                              className="inline-flex items-center justify-center min-w-[14px] h-3.5 px-0.5 rounded-sm bg-white/25 text-[8px] font-bold shrink-0"
                              title="Make-up students this session"
                            >
                              M
                            </span>
                          )}
                          {showSportGlyphsOnChips ? (
                            <CalendarChipSportGlyph
                              lesson={occ.lesson}
                              academySports={academySports}
                              enabled={showSportGlyphsOnChips}
                              variant="light"
                            />
                          ) : null}
                          <span className="min-w-0 truncate">{occ.lessonName || occ.lesson.name}</span>
                        </button>
                      );

                      if (occurrenceChipOpensDetail(occ)) {
                        return (
                          <CalendarScheduleHint
                            key={`${occ.sourceType}-${occ.lessonId}-${idx}`}
                            label={tooltip}
                          >
                            {chipButton}
                          </CalendarScheduleHint>
                        );
                      }
                      return (
                        <Popover key={`${occ.sourceType}-${occ.lessonId}-${idx}`}>
                          <CalendarScheduleHint label={tooltip}>
                            <PopoverTrigger asChild>{chipButton}</PopoverTrigger>
                          </CalendarScheduleHint>
                          <PopoverContent className="w-80" align="start">
                            <LessonOccurrenceDayPopover
                              occurrence={occ}
                              hasCoach={hasCoach(occ.lesson)}
                              coaches={coaches}
                              hasCoachAvailabilityWarning={
                                coachAvailabilityWarnings.get(occ.lesson.id)?.has(occ.date.getDay()) ?? false
                              }
                            />
                          </PopoverContent>
                        </Popover>
                      );
                    })}
                    {dayOccurrences.length > maxVisibleOccurrences ? (
                      <span className="w-full rounded px-0.5 py-px text-[8px] font-medium leading-tight text-muted-foreground sm:px-1 sm:text-[10px]">
                        +{dayOccurrences.length - maxVisibleOccurrences} more
                      </span>
                    ) : null}
                  </div>
                </>
              );

              return dayPopoverEnabled ? (
                <LessonCalendarDayPopover
                  key={key}
                  day={day}
                  occurrences={dayOccurrences}
                  getDotChipProps={getDotChipProps}
                  onLessonOccurrenceClick={onLessonOccurrenceClick}
                  onScheduleOccurrenceClick={onScheduleOccurrenceClick}
                  coaches={coaches}
                  coachAvailabilityWarnings={coachAvailabilityWarnings}
                  hasCoach={hasCoach}
                  tooltipContext={tooltipContext}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    className={dayCellClassName}
                    aria-label={lessonCalendarDayPopoverAriaLabel(day, dayOccurrences.length)}
                  >
                    {dayCellInner}
                  </div>
                </LessonCalendarDayPopover>
              ) : (
                <div key={key} className={dayCellClassName}>
                  {dayCellInner}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 text-[10px] text-gray-600 dark:text-gray-400 sm:flex sm:flex-wrap sm:gap-4 sm:text-xs">
            {dayPopoverEnabled && (
              <span className="col-span-2 sm:col-span-1 text-gray-500 dark:text-gray-400 italic">
                Click a day to view lessons
              </span>
            )}
          </div>
        </div>
    </div>
  );
}
